import React, { useEffect, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { getAdminStats, getChartData } from '../api';
import { User, Activity, CreditCard, ShoppingCart, TrendingUp } from 'lucide-react';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const Dashboard = () => {
    const { excludeCompany } = useOutletContext();
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeChartTab, setActiveChartTab] = useState('signups');
    const [granularity, setGranularity] = useState('month');
    const [chartData, setChartData] = useState([]);
    const [chartLoading, setChartLoading] = useState(false);

    useEffect(() => {
        fetchStats();
    }, [excludeCompany]);

    useEffect(() => {
        fetchChartData();
    }, [activeChartTab, granularity, excludeCompany]);

    const fetchStats = async () => {
        try {
            const res = await getAdminStats(excludeCompany);
            if (res.data.success) {
                setStats(res.data.data);
            }
        } catch (error) {
            console.error("Failed to fetch dashboard stats", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchChartData = async () => {
        setChartLoading(true);
        try {
            const res = await getChartData(activeChartTab, granularity, excludeCompany);
            if (res.data.success) {
                const formattedData = res.data.data.map(item => ({
                    ...item,
                    label: formatPeriodLabel(item.period, granularity)
                }));
                setChartData(formattedData);
            }
        } catch (error) {
            console.error("Failed to fetch chart data", error);
        } finally {
            setChartLoading(false);
        }
    };

    const formatPeriodLabel = (period, gran) => {
        if (!period) return '';
        const date = new Date(period);
        switch (gran) {
            case 'year': return date.getFullYear().toString();
            case 'month': return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            case 'day': return `${date.getMonth() + 1}/${date.getDate()}`;
            case 'hour': return `${date.getMonth() + 1}/${date.getDate()} ${date.getHours()}:00`;
            default: return period;
        }
    };

    const formatNumber = (num) => {
        return num ? num.toLocaleString() : 0;
    };

    const StatCard = ({ title, value, subValues, icon: Icon, colorClass }) => (
        <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
            <div className={`p-2 rounded-full w-fit ${colorClass.bg} ${colorClass.text} mb-2`}>
                <Icon size={20} />
            </div>
            <h3 className="text-gray-500 text-xs font-medium uppercase tracking-wide">{title}</h3>
            <p className="text-2xl font-bold text-gray-900 mt-1">{formatNumber(value)}</p>
            <div className="mt-2 space-y-0.5">
                {subValues.map((sub, idx) => (
                    <div key={idx} className="flex items-center text-xs">
                        <span className="text-gray-500 mr-1">{sub.label}:</span>
                        <span className="font-semibold text-gray-700">{formatNumber(sub.value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );

    const chartTabs = [
        { id: 'signups', label: '가입자수', type: 'line' },
        { id: 'logins', label: '접속자수', type: 'line' },
        { id: 'optimizations', label: '최적화 수', type: 'line' },
        { id: 'subscribers', label: '구독 가입자', type: 'bar' },
        { id: 'addons', label: 'Add-on 결제', type: 'bar' },
    ];

    const granularityOptions = [
        { id: 'year', label: '연' },
        { id: 'month', label: '월' },
        { id: 'day', label: '일' },
        { id: 'hour', label: '시간' },
    ];

    const currentTab = chartTabs.find(t => t.id === activeChartTab);

    if (loading) return (
        <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
    );

    return (
        <div className="space-y-6">
            <h1 className="text-2xl font-bold text-gray-800">Dashboard</h1>

            {/* Stat Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <StatCard
                    title="총 가입자수"
                    value={stats?.total_users}
                    subValues={[
                        { label: "금일 가입자수", value: stats?.today_new_users }
                    ]}
                    icon={User}
                    colorClass={{ bg: 'bg-blue-50', text: 'text-blue-600' }}
                />
                <StatCard
                    title="총 접속자수"
                    value={stats?.total_logins}
                    subValues={[
                        { label: "금일 접속자수", value: stats?.today_logins }
                    ]}
                    icon={Activity}
                    colorClass={{ bg: 'bg-orange-50', text: 'text-orange-600' }}
                />
                <StatCard
                    title="총 최적화 수"
                    value={stats?.total_optimizations}
                    subValues={[
                        { label: "금일 최적화 수", value: stats?.today_optimizations }
                    ]}
                    icon={TrendingUp}
                    colorClass={{ bg: 'bg-purple-50', text: 'text-purple-600' }}
                />
                <StatCard
                    title="현재 Basic이상 구독자 수"
                    value={stats?.active_paid_subscribers}
                    subValues={[
                        { label: "이번달 구독자 수", value: stats?.new_paid_subscribers_month }
                    ]}
                    icon={CreditCard}
                    colorClass={{ bg: 'bg-green-50', text: 'text-green-600' }}
                />
                <StatCard
                    title="Credit 추가 구매자수"
                    value={stats?.total_addon_credit_users}
                    subValues={[
                        { label: "이번달 추가 구매자수", value: stats?.new_addon_credit_users_month }
                    ]}
                    icon={ShoppingCart}
                    colorClass={{ bg: 'bg-indigo-50', text: 'text-indigo-600' }}
                />
            </div>

            {/* Chart Section */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                {/* Tab Header */}
                <div className="flex flex-wrap justify-between items-center mb-4 gap-2">
                    <div className="flex gap-1">
                        {chartTabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveChartTab(tab.id)}
                                className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${activeChartTab === tab.id
                                    ? 'bg-blue-500 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex gap-1">
                        {granularityOptions.map(opt => (
                            <button
                                key={opt.id}
                                onClick={() => setGranularity(opt.id)}
                                className={`px-2 py-1 text-xs rounded-md transition-colors ${granularity === opt.id
                                    ? 'bg-gray-700 text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                            >
                                {opt.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Chart */}
                <div className="h-64">
                    {chartLoading ? (
                        <div className="flex justify-center items-center h-full">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        </div>
                    ) : chartData.length === 0 ? (
                        <div className="flex justify-center items-center h-full text-gray-400">
                            No data available
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            {currentTab?.id === 'subscribers' ? (
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Legend />
                                    <Bar dataKey="Basic" fill="#3b82f6" radius={[4, 4, 0, 0]} name="Basic" />
                                    <Bar dataKey="Pro" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Pro" />
                                </BarChart>
                            ) : currentTab?.type === 'bar' ? (
                                <BarChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Bar dataKey="count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                                </BarChart>
                            ) : (
                                <LineChart data={chartData}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                                    <YAxis tick={{ fontSize: 11 }} />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="count"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={{ fill: '#3b82f6', strokeWidth: 2, r: 3 }}
                                    />
                                </LineChart>
                            )}
                        </ResponsiveContainer>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
